import { useApiPost, useApiPut } from '@/api/main/query'
import { BaseProfileCard, VerificationStatus } from '@/features/profile/ui'
import { DialogCopyBots } from '@/features/profile/ui/dialogs/DialogCopyBots'
import { BaseButton } from '@/shared/components/BaseButton/BaseButton'
import { PasswordInput } from '@/shared/components/PasswordInput/PasswordInput'
import { passwordValidation } from '@/shared/constants/validationRules'
import { useActiveUser, useToast } from '@/shared/hooks'
import { useAppSelector } from '@/store/hooks'
import { profileAPI } from '@/widgets/profile/api/endpoints'
import { SendData } from '@/widgets/profile/model/types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { selectTrader } from '@/entities/trader'
import { BaseDialog, BaseInput } from '@/shared/components'
import QRCode from 'qrcode'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'
import { USER_ROLE_KEY } from '@/features/auth'
import { Roles } from '@/entities/roles'
import { getStorageItem } from '@/shared/lib'
import { selectMerchant } from '@/entities/merchant'
import verificated from '@/shared/assets/icons/profile/verificated.svg'

type FormData = {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}
type data2FA = { email: string; password: string; token2FA?: string }
type generated2fa = {
  is2FAEnabled?: boolean
  base32key: string
  otpauthUrl: string
}
export const Safe = () => {
  const { isVerified } = useActiveUser()
  const trader = useAppSelector(selectTrader)
  const merchant = useAppSelector(selectMerchant)
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const user = userRole === Roles.Merchant ? merchant : trader
  const [dialogCopyBotsVisible, setDialogCopyBotsVisible] = useState(false)
  const [dialogPasswordVisible, setDialogPasswordVisible] = useState(false)
  const [dialogValidate2fa, setDialogValidate2fa] = useState(false)

  const [has2FA, setHas2FA] = useState(user?.is2FARequired ?? false)
  const [password, setPassword] = useState('')
  const [token2FA, setToken2FA] = useState('')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [verificationCode, setVerificationCode] = useState('')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormData>()

  const { addToast } = useToast()

  const { mutate: changePassword, isPending: isPendingChangePassword } = useApiPut<void, SendData>(
    profileAPI.changePassword
  )

  const { mutate: enable2FA, isPending: isPendingEnable2FA } = useApiPost<generated2fa, data2FA>(
    '/v1/auth/2fa/generate'
  )
  const { mutate: validate2FA, isPending: isPendingValidate2FA } = useApiPost<void, data2FA>(
    '/v1/auth/2fa/validate'
  )

  const onSubmit = (data: FormData) => {
    changePassword(
      {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          addToast('Успех!', 'Пароль изменен', 'success')
          reset()
        },
        onError: (error) => {
          addToast('Ошибка!', error.message, 'error')
        },
      }
    )
  }

  const onChange2fa = () => {
    if (!password) {
      addToast('Ошибка!', 'Введите пароль', 'error')
      return
    }

    enable2FA(
      {
        password,
        email: user?.email ?? '',
      },
      {
        onSuccess: (data) => {
          setToken2FA(data.base32key)
          QRCode.toDataURL(data.otpauthUrl)
            .then((url) => {
              setQrCodeUrl(url)
              setDialogPasswordVisible(false)
              setDialogValidate2fa(true)
            })
            .catch((err) => {
              addToast('Ошибка!', `Ошибка при генерации QR:  ${err.message}`, 'error')
            })
        },
        onError: (error) => {
          setPassword('')
          addToast('Ошибка!', error.message, 'error')
        },
      }
    )
  }

  const onValidate2fa = () => {
    if (verificationCode.length !== 6) {
      addToast('Ошибка!', 'Введите 6-значный код', 'error')
      return
    }

    validate2FA(
      {
        password,
        email: user?.email ?? '',
        token2FA: verificationCode,
      },
      {
        onSuccess: () => {
          setToken2FA('')
          setQrCodeUrl('')
          setVerificationCode('')
          setDialogValidate2fa(false)
          addToast('Успех!', 'Двухфакторная аутентификация подключена', 'success')
          setHas2FA(true)
        },
        onError: (error) => {
          setVerificationCode('')
          addToast('Ошибка!', error.message, 'error')
        },
      }
    )
  }

  return (
    <BaseProfileCard title="Безопасность">
      <div>
        <h4 className="mb-4 font-medium text-text-main dark:text-white">Смена пароля</h4>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 mb-8 md:pr-10">
            <PasswordInput
              placeholder="Введите текущий пароль"
              {...register('oldPassword', { required: 'Введите текущий пароль' })}
              error={errors.oldPassword?.message}
            />

            <PasswordInput
              placeholder="Введите новый пароль"
              {...register('newPassword', passwordValidation)}
              error={errors.newPassword?.message}
            />

            <PasswordInput
              placeholder="Повторите новый пароль"
              {...register('confirmPassword', {
                required: 'Повторите новый пароль',
                validate: (value) => value === watch('newPassword') || 'Пароли не совпадают',
              })}
              error={errors.confirmPassword?.message}
            />
          </div>
          <BaseButton type="submit" disabled={isPendingChangePassword}>
            Сменить пароль
          </BaseButton>
        </form>
      </div>
      <div>
        <h4 className="mb-2 font-medium text-text-main dark:text-white">Верификация KYC</h4>
        <p className="mb-6 text-text-secondary text-sm">
          Данному пользователю необходимо пройти процедуру верификации KYC
        </p>
        <div className="flex items-center gap-2 md:gap-6">
          {!isVerified && (
            <BaseButton onClick={() => setDialogCopyBotsVisible(true)}>Пройти</BaseButton>
          )}

          <DialogCopyBots
            modalVisible={dialogCopyBotsVisible}
            setModalVisible={setDialogCopyBotsVisible}
          />
          <VerificationStatus status={isVerified ? 'done' : 'failed'} />
        </div>
      </div>
      <div>
        <h4 className="mb-2 font-medium text-text-main dark:text-white">
          Двухфакторная аутентификация
        </h4>
        <p className="mb-6 text-text-secondary text-sm">
          Дополнительно защитите свой аккаунт с помощью приложения Google Authenticator (
          <a
            href="https://apps.apple.com/ru/app/google-authenticator/id388497605"
            className="underline text-primary"
          >
            iOS
          </a>
          ,&nbsp;
          <a
            href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=ru&gl=US&pli=1"
            className="underline text-primary"
          >
            Android
          </a>
          )
        </p>
        <div className="flex gap-1 md:gap-6">
          {!has2FA ? (
            <>
              <BaseButton onClick={() => setDialogPasswordVisible(true)}>Подключить</BaseButton>

              <BaseDialog
                isLoading={isPendingEnable2FA}
                title="Подключение двухфакторной авторизации"
                isVisible={dialogPasswordVisible}
                actionButtonName="Подключить"
                closeButton={true}
                onAction={onChange2fa}
                onClose={() => {
                  setDialogPasswordVisible(false)
                  setPassword('')
                }}
              >
                <BaseInput
                  type="password"
                  placeholder="Введите пароль"
                  onChange={(t) => setPassword(t.target.value)}
                />
              </BaseDialog>

              <BaseDialog
                title="Подключение двухфакторной авторизации"
                isVisible={dialogValidate2fa}
                isLoading={isPendingValidate2FA}
                actionButtonName="Подтвердить"
                closeButton={true}
                onAction={onValidate2fa}
                onClose={() => {
                  setDialogValidate2fa(false)
                  setVerificationCode('')
                  setQrCodeUrl('')
                  setToken2FA('')
                  setPassword('')
                }}
              >
                <div className="flex flex-col text-sm gap-3">
                  <span>Отсканируйте qr-код:</span>
                  <img src={qrCodeUrl} alt="qr code" className="w-30 h-30 mx-auto" />
                  <span className="text-text-secondary">
                    или скопируйте ключ и вставьте его в приложении Google Authenticator:{' '}
                    <CopyBlock text={token2FA} className="text-primary">
                      {token2FA}
                    </CopyBlock>
                  </span>
                </div>
                <BaseInput
                  placeholder="Введите 6-значный код из приложения"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '')
                    if (value.length <= 6) setVerificationCode(value)
                  }}
                  maxLength={6}
                />
                <span className="text-text-secondary text-xs">
                  Код обновляется каждые 30 секунд. Если код не подходит, подождите нового.
                </span>
              </BaseDialog>
            </>
          ) : (
            <div
              className="flex gap-1 items-center font-medium text-base"
            >
              <span
                className='text-text-verificated'
              >
                Подключена
              </span>
              <img
                src={verificated}
                className="w-5 h-5 md:w-6 md:h-6"
              />
            </div>
          )}
        </div>
      </div>
    </BaseProfileCard>
  )
}
